import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { MatCardModule } from '@angular/material/card';
import { AvatarBlockModule } from '../shared/avatar-block/avatar-block.module';
import { AsyncPipe, KeyValuePipe, NgForOf, NgIf } from '@angular/common';
import { MatGridListModule } from '@angular/material/grid-list';
import { CardNestedCardDirective } from '../shared/cards/card.directive';
import { AnimatedDelayDirective, AnimatedDirective } from '../shared/directives/animated.directive';
import { OnboardingCard } from '../../../models/entities/onboarding-card';
import { AssignedSalesRep } from '../../../models/entities/assigned-sales-rep';
import { originalOrderFunction } from '../../../functions/original-order';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { Observable } from 'rxjs';
import { PropertyManagementCompany } from '../../../models/entities/property-management-company';
import { PropertyManagementCompanyDataService } from '../../../services/property-management-company/property-manager-company-data.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LegfiPhonePipeModule } from '../../../pipes/format/legfi-phone.module';
import { MatIconModule } from '@angular/material/icon';
import { DemoSchedulerService } from '../../../services/demo-scheduler.service';
import { PropertyManagementCompanyService } from '../../../services/property-management-company/property-management-company.service';

export enum OnboardingStep {
    ADD_COMMUNITY,
    ADD_TEAM_MEMBER,
    UPLOAD_LOGO,
}

@UntilDestroy()
@Component({
    selector: 'app-launchpad',
    templateUrl: './launchpad.component.html',
    styleUrls: ['./launchpad.component.scss'],
    standalone: true,
    imports: [
        PageHeaderComponent,
        MatCardModule,
        AvatarBlockModule,
        MatGridListModule,
        NgForOf,
        KeyValuePipe,
        RouterLink,
        MatButtonModule,
        AsyncPipe,
        AnimatedDirective,
        CardNestedCardDirective,
        LegfiPhonePipeModule,
        MatIconModule,
        NgIf,
        AnimatedDelayDirective,
    ],
})
export class LaunchpadComponent implements OnInit
{
    @ViewChildren('onboardingCard', {read: ElementRef}) onboardingCardRef: QueryList<ElementRef>;

    pmc$: Observable<PropertyManagementCompany>;
    cards: { [prop: string]: OnboardingCard } = {};
    assignedSalesRep: AssignedSalesRep;
    videoSrc: SafeResourceUrl;
    isLoadingInitialCardData = true;

    readonly howToUrl = 'https://intercom.help/payhoa/en/';
    readonly originalOrder = originalOrderFunction;

    constructor(
            private _activatedRoute: ActivatedRoute,
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _demoScheduler: DemoSchedulerService,
            private _sanitizer: DomSanitizer,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    get isLoadingInitialRepData() {
        return this._demoScheduler.loading$.asObservable();
    }

    ngOnInit() {
        this.pmc$ = this._propertyManagementCompanyData.pmc$;

        this.buildCards();
        this.setupVideo();

        this._demoScheduler.assignedSalesRep$.asObservable().pipe(
                untilDestroyed(this),
        ).subscribe({
            next: (rep) => this.assignedSalesRep = rep,
        });

        this._propertyManagementCompanyService.getPropertyManagementCompany(
                this.pmcId,
                ['organizations.assignedSalesRep'],
            ).pipe(
                untilDestroyed(this),
        ).subscribe({
            next: (pmc) => {
                if (pmc.organizations[0]?.assignedSalesRep) {
                    this._demoScheduler.assignSalesRepForDemo(pmc.organizations[0].assignedSalesRep);

                    // for now, still leveraging the "first" query param to indicate whether we pop the dialog
                    if (!!this._activatedRoute.snapshot.queryParamMap.get('first')) {
                        this._demoScheduler.scheduleDemo();
                    }
                }
            },
        });

        this.isLoadingInitialCardData = false;
    }

    onDemoSchedulerClick() {
        this._demoScheduler.scheduleDemo();
    }

    private buildCards() {
        this.cards[OnboardingStep.ADD_COMMUNITY] = {
            title: 'Create a New Community',
            description: 'Create your first community.',
            actionUrl: ['..', 'communities', 'add'],
            isDisabled: false,
            isDone: false,
            svg: 'v2:fi-br-building',
        };
        this.cards[OnboardingStep.ADD_TEAM_MEMBER] = {
            title: 'Add Team Members',
            description: 'Invite community managers.',
            actionUrl: ['..', 'team', 'add'],
            isDisabled: false,
            isDone: false,
            svg: 'v2:fi-br-team',
        };
        /*this.cards[OnboardingStep.UPLOAD_LOGO] = {
            title: 'Upload Organization Logo',
            description: 'Personalize your property management company by uploading your organization’s logo.',
            actionUrl: ['..', 'settings'],
            isDisabled: false,
            isDone: false,
            svg: 'v2:fi-br-picture',
        };*/
    }

    private setupVideo() {
        const url = 'https://player.vimeo.com/video/350964325?h=a5a24b0c10&title=0&byline=0&portrait=0';
        this.videoSrc = this._sanitizer.bypassSecurityTrustResourceUrl(url);
    }
}
